import type { Session } from '@orgus/protocol';
import { CircleDollarSign, Gauge as GaugeIcon, ReceiptText } from '@orgus/ui/icons';
import { useMemo } from 'react';
import {
  EmptyState,
  MeterBar,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  cn,
  formatCompact,
  formatInt,
  formatPercent,
} from '@orgus/ui';
import { useCosts } from '../costs/useCosts.ts';
import { modelLabel } from '../sessions/labels.ts';
import { costView, formatUsd } from './model.ts';

function TokenFacts({ s }: { s: Session }) {
  return (
    <dl className="grid grid-cols-3 gap-3 rounded-card border border-border-subtle p-3 text-left">
      <div>
        <dt className="text-caption text-fg-subtle">Input tokens</dt>
        <dd className="text-ui text-fg num">{formatInt(s.tokens.input)}</dd>
      </div>
      <div>
        <dt className="text-caption text-fg-subtle">Output tokens</dt>
        <dd className="text-ui text-fg num">{formatInt(s.tokens.output)}</dd>
      </div>
      <div>
        <dt className="text-caption text-fg-subtle">Cache reads</dt>
        <dd className="text-ui text-fg num">{formatInt(s.tokens.cacheRead)}</dd>
      </div>
    </dl>
  );
}

const BUDGET_STATE = {
  ok: { label: 'Within budget', cls: 'text-fg-subtle', bar: 'bg-fg-subtle' },
  soft: { label: 'Near the limit', cls: 'text-waiting', bar: 'bg-waiting' },
  hard: { label: 'Over the limit', cls: 'text-failed', bar: 'bg-failed' },
} as const;

/** What the session cost, per model, against its budget, from the costs module. */
/** `showTokens` off (the drawer's Details): no spend and token headline, only the breakdown. */
export function CostTab({ session, showTokens = true }: { session: Session; showTokens?: boolean }) {
  const costs = useCosts();
  const v = useMemo(() => costView(costs, session.id), [costs, session.id]);

  if (!costs) {
    return (
      <div className="flex flex-col gap-4">
        <EmptyState
          icon={<CircleDollarSign />}
          title="Cost tracking is not on for this hub"
          description="The token counts below come from the transcript."
        />
        {showTokens && <TokenFacts s={session} />}
      </div>
    );
  }
  if (!v) {
    return (
      <div className="flex flex-col gap-4">
        <EmptyState
          icon={<ReceiptText />}
          title="No cost recorded for this session yet"
          description="It appears within a minute of the next activity."
        />
        {showTokens && <TokenFacts s={session} />}
      </div>
    );
  }

  const { usage, models, budget } = v;
  const pct = budget ? Math.min(1, budget.limit > 0 ? budget.spent / budget.limit : 0) : 0;
  const bs = budget ? BUDGET_STATE[budget.state] : null;
  return (
    <div className="flex flex-col gap-4">
      {/* Inside the drawer's Details the spend and tokens are already facts above: only the breakdown. */}
      {showTokens && (
      <div className="flex flex-wrap items-end gap-x-6 gap-y-2">
        <div>
          <p className="text-caption text-fg-subtle">Spent</p>
          <p className="text-2xl font-semibold text-fg num">{formatUsd(usage.total.cost)}</p>
        </div>
        <div>
          <p className="text-caption text-fg-subtle">Tokens</p>
          <p className="text-lg text-fg num">{formatCompact(usage.total.input + usage.total.output)} in and out</p>
        </div>
        {usage.rate > 0 && (
          <div>
            <p className="text-caption text-fg-subtle">Rate, last 10 min</p>
            <p className="text-lg text-fg num">{formatCompact(usage.rate)} tokens per min</p>
          </div>
        )}
      </div>
      )}
      {budget && bs && (
        <section aria-label="Budget" className="flex flex-col gap-2 rounded-card border border-border bg-raised p-3">
          <div className="flex items-center gap-2 text-sm">
            <GaugeIcon className={cn('size-4', bs.cls)} aria-hidden />
            <span className={cn('font-medium', bs.cls)}>{bs.label}</span>
            <span className="ml-auto text-fg-muted num">
              {formatUsd(budget.spent)} of {formatUsd(budget.limit)}
            </span>
          </div>
          <div className="h-1 overflow-hidden rounded-pill bg-surface" role="meter" aria-label="Budget used" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct * 100)}>
            <div className={cn('h-full rounded-pill', bs.bar)} style={{ width: `${pct * 100}%` }} />
          </div>
          <p className="text-caption text-fg-subtle">
            {budget.onLimit === 'stop' ? 'At the limit, Orgus stops this session from doing more work.' : 'At the limit, Orgus warns; the session keeps working.'}
          </p>
        </section>
      )}

      <CostPerModel models={models} total={usage.total} note={`${v.sourceLabel}, prices of ${usage.pricingVersion}`} />
    </div>
  );
}

type ModelCost = { model: string; input: number; output: number; cacheRead: number; cost: number; share: number };

/** The edge cells sit on the drawer's content edge, not the row inset. */
const EDGE = 'first:pl-0 last:pr-0';

/**
 * Spend per model: the model is the name, token counts muted, the cost bright with its share as
 * a thin grey bar and a dim percent. A total row only when there is more than one model.
 */
function CostPerModel({ models, total, note }: { models: ModelCost[]; total: Omit<ModelCost, 'model' | 'share'>; note: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <Table aria-label="Cost per model">
        <TableHeader>
          <TableRow>
            <TableHead className={EDGE}>Model</TableHead>
            <TableHead numeric className={EDGE}>Input</TableHead>
            <TableHead numeric className={EDGE}>Output</TableHead>
            <TableHead numeric className={cn(EDGE, 'hidden sm:table-cell')}>Cache reads</TableHead>
            <TableHead numeric className={EDGE}>Cost</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {models.map((m) => (
            <TableRow key={m.model}>
              <TableCell primary className={EDGE}>{modelLabel(m.model)}</TableCell>
              <TableCell numeric className={EDGE}>{formatCompact(m.input)}</TableCell>
              <TableCell numeric className={EDGE}>{formatCompact(m.output)}</TableCell>
              <TableCell numeric className={cn(EDGE, 'hidden sm:table-cell')}>{formatCompact(m.cacheRead)}</TableCell>
              <TableCell numeric className={EDGE}>
                <span className="inline-flex items-center justify-end gap-2">
                  <MeterBar value={m.share} tone="neutral" label={`Share of the session's cost: ${formatPercent(m.share)}`} className="w-8 opacity-60" />
                  <span className="w-8 text-caption text-fg-subtle">{formatPercent(m.share)}</span>
                  <span className="text-fg">{formatUsd(m.cost)}</span>
                </span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        {models.length > 1 && (
          <TableFooter>
            <TableRow>
              <TableCell className={EDGE}>Total</TableCell>
              <TableCell numeric className={EDGE}>{formatCompact(total.input)}</TableCell>
              <TableCell numeric className={EDGE}>{formatCompact(total.output)}</TableCell>
              <TableCell numeric className={cn(EDGE, 'hidden sm:table-cell')}>{formatCompact(total.cacheRead)}</TableCell>
              <TableCell numeric className={EDGE}>{formatUsd(total.cost)}</TableCell>
            </TableRow>
          </TableFooter>
        )}
      </Table>
      {/* One line under the table: where the numbers come from (a caption would widen the table). */}
      <p className="truncate text-caption text-fg-subtle" title={note}>
        {note}
      </p>
    </div>
  );
}
